// Every Study Hall string the page shows (DESIGN.md section 18). The server sends its own fixed
// copy in `err.msg` and `bye.msg` (src/server/lib/chat/copy.js); these are the client's copy and
// the fallbacks for when a frame carries none. No string here may contain an em dash
// (test/chat.client.test.mjs checks), and none may ever quote a matched word back.

export const NAME = "Study Hall";

// 18.1 Rules card
export const RULES_TITLE = "Before you join Study Hall";
export const AGE_PROMPT = "When were you born?";
export const MONTH = "Month";
export const YEAR = "Year";
export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const RULES = [
	"Be kind. No bullying, hate or harassment.",
	"Keep it school-appropriate.",
	"Never share personal info: your full name, school, phone, address, socials or photos.",
	"Moderators can see everything posted here. Messages are kept for 30 days.",
	"You must be 13 or older to use Study Hall.",
];
export const AGREE = "I agree";
export const RULES_BUTTON = "Rules";
export const RULES_HEADING = "Study Hall rules";

// 18.2 Automod reasons (by category; the server's err.msg wins when present)
export const WORD_BLOCKED = "That message has a word Study Hall doesn't allow. Try saying it another way.";
export const WORD_WAIT = (s) => "You can send again in " + s + (s === 1 ? " second." : " seconds.");
export const WORD_READY = "You can send again.";
export const NAME_WORD = "That name has a word Study Hall doesn't allow. Try another.";
export const AUTOMOD = {
	// Every listed word gets the same calm line: it never says which word or what kind.
	slur: WORD_BLOCKED,
	sexual: WORD_BLOCKED,
	profanity: WORD_BLOCKED,
	spam: "That reads as spam. Try again in your own words.",
	dup: "You just said that.",
	harass: "That's not okay to say here. Be kind.",
	contact: "For your safety, don't share social accounts or invite links in Study Hall.",
	pii: "For your safety, don't share phone numbers, emails or addresses here.",
	probe: "Please don't ask people for personal info like their age, school, location or photos.",
	link: "That link can't be posted here.",
};
export const LINK_PROBATION = "Links unlock after your first day in Study Hall.";
export const LINK_SHORTENER = "Please post the full link, not a shortened one.";
export const LINK_COUNT = "Up to 3 links per message.";

// 18.3 States and banners
export const WAIT = (s) => "Wait " + s + "s";
export const BUSY = "This room is busy. Try again in a moment.";
export const TIMED_OUT = (time, reason) => "You can't post until " + time + "." + (reason ? " Reason: " + reason : "");
export const BANNED = (reason) => "You've been removed from Study Hall." + (reason ? " Reason: " + reason + "." : "") + " Think this is a mistake? Email hello@ajhsvault.org.";
export const LOCKDOWN_NEW = "Study Hall is in quiet mode for newer accounts right now. You can still read.";
export const LOCKDOWN_ALL = "Only moderators can post right now.";
export const LOCKED = "This room is read-only right now.";
export const ANNOUNCE_ONLY = "Only the Re:Vault team posts here. You can still react.";
export const READONLY = "Study Hall is read-only right now. You can still read.";
export const RECONNECTING = "Reconnecting...";
export const BACK_ONLINE = "Back online.";
export const WS_BLOCKED = "Study Hall can't connect on this network right now. The rest of Re:Vault still works.";
export const CLOSED = "Study Hall is taking a short break.";
export const BETA = "Study Hall opens soon.";
export const TRY_AGAIN = "Try again";
export const REPLACED = "Study Hall is open in another tab.";
export const USE_HERE = "Use here";

// 18.4 Care text
export const SUPPORT = "It sounds like things might be really hard right now. You can call or text 988 anytime (US) to talk with someone, or text HOME to 741741. If you're in danger, call 911. Talking to a trusted adult can help too.";
export const REPORT_THANKS = "Thanks. A moderator will review it.";
export const REPORT_SELF_HARM = "If someone is in immediate danger, call 911. You can call or text 988 anytime.";

// 18.5 Placeholders and row labels
export const PLACEHOLDER = (room) => "Message #" + room;
export const PLACEHOLDER_REPLY = (h) => "Reply to " + h;
export const COUNTER = (n, max) => n + "/" + max;
export const ST_LABEL = { 1: "Message deleted", 2: "Removed by a moderator", 3: "Hidden while a moderator reviews it", 4: "Sent to a moderator first", 5: "Not posted" };
// A held message as its author sees it, and as staff see it.
export const HELD_OWN = "Sent to a moderator first. Only you can see this until it's approved.";
export const HELD_STAFF = "Waiting for a moderator. Only the sender and staff can see this.";
export const HELD_GONE_STAFF = "Not posted. Only the sender and staff can see this.";
export const BLOCKED_ROW = "Message from someone you blocked.";
export const SHOW = "Show";
export const EDITED = "(edited)";

// 16.2 Page states
export const CONNECTING = "Connecting...";
export const ACCOUNTS_OFF = "Study Hall isn't available right now.";
export const SIGNED_OUT_TITLE = "Study Hall";
export const SIGNED_OUT = "Live study rooms for math, science and English, a shared focus timer, and real moderators. Sign in to read and post.";
export const SIGN_IN = "Sign in with Google";
export const SIGN_IN_FAILED = "Sign-in isn't available right now.";
export const SIGNED_EXPIRED = "Your sign-in ended. Sign in again to keep using Study Hall.";
export const GATE_NOTE = "One quick step before you join.";
export const UPDATED = "Study Hall was updated. Reload to keep going.";
export const RELOAD = "Reload";
export const LOADING = "Loading…";
export const UNDER_13 = "Study Hall is for ages 13 and up. You can keep using everything else in Re:Vault.";
export const HANDLE_TITLE = "Pick a name for Study Hall";
export const HANDLE_TAKEN = "Someone already uses that name. Try another.";
export const HANDLE_HINT = "2 to 24 characters. Everyone in Study Hall sees this name, so don't use your real full name.";
export const HANDLE_LABEL = "Name";
export const SAVE = "Save";
export const CANCEL = "Cancel";
export const CLOSE = "Close";
export const OK = "OK";

// 16.5 Links
export const LINK_COPIED = "Link copied. Open it from a Re:Vault tab.";

// Main layout
export const ROOMS = "Rooms";
export const HERE = (n) => n + " here";
export const SLOW = (s) => "Slow mode " + (s >= 60 ? s / 60 + "m" : s + "s");
export const LOCKED_TAG = "Locked";
export const TODAY = "Today";
export const YESTERDAY = "Yesterday";
export const NEW_DIVIDER = "New";
export const NEW_PILL = (n) => (n === 1 ? "1 new message" : n + " new messages");
export const LOAD_OLDER = "Load older messages";
export const JUMP_LATEST = "Jump to latest";
export const SENDING = "Sending...";
export const QUEUED = "Waiting to send...";
export const SEND = "Send";
export const REPLYING_TO = (h) => "Replying to " + h;
export const EDITING = "Editing message";
export const REPLY_TO_PREVIEW = (h) => "replying to " + h;
export const REPLY_GONE = "Original message unavailable";
export const EMPTY_ROOM = "No messages yet. Say hi!";
export const EMPTY_BY_KIND = {
	announce: "News from the Re:Vault team shows up here.",
	quiet: "Heads down. Start a focus block, then check in when it ends.",
};
export const MENTIONED = "Mentions you";
export const DENSITY = "Density";
export const COZY = "Cozy";
export const COMPACT = "Compact";
export const CHANGE_NAME = "Change name";
export const ME_HINT = "This is the name everyone in Study Hall sees. Moderators never see your email.";
export const DELETE_DATA = "Delete my Study Hall data";
export const DELETE_DATA_Q = "This deletes your Study Hall messages, reactions, blocks and read history. Messages that moderators are reviewing are removed when the review ends. This can't be undone.";
export const DELETE_DATA_DONE = "Your Study Hall data was deleted.";
export const DOWNLOAD_DATA = "Download my Study Hall data";
export const DOWNLOAD_STARTED = "Your Study Hall data is downloading.";
export const NAME_NEXT = (date) => "You can change your name again on " + date + ".";
export const DELETE = "Delete";
export const TYPING = (who, others) => {
	if (!who.length) return "";
	if (others > 0) return who.slice(0, 2).join(", ") + " and " + others + (others === 1 ? " other" : " others") + " are typing";
	if (who.length === 1) return who[0] + " is typing";
	if (who.length === 2) return who[0] + " and " + who[1] + " are typing";
	return who[0] + ", " + who[1] + " and " + who[2] + " are typing";
};
export const OUTBOX_FULL = "Hold on: a few messages are still waiting to send.";
export const NOTICE_TITLE = "Just for you";
export const URGENT_TOAST = "New urgent report in the mod queue.";
export const DISMISS = "Dismiss";
export const PROBATION_LINKS = LINK_PROBATION;

// Toolbar and dialogs
export const T = {
	reply: "Reply", react: "React", edit: "Edit", del: "Delete", report: "Report", block: "Block", unblock: "Unblock",
	remove: "Remove", restore: "Restore", timeout: "Timeout", ban: "Ban", user: "View user", more: "More reactions", card: "Name card",
	approve: "Approve",
};
export const CONFIRM_DELETE = "Delete this message?";
export const CONFIRM_BLOCK = (h) => "Block " + h + "? You won't see their messages or mentions. They aren't told.";
export const CONFIRM_UNBLOCK = (h) => "Unblock " + h + "?";
export const REPORT_TITLE = "Report message";
export const REPORT_CATS = [
	["bullying", "Bullying or harassment"],
	["sexual", "Sexual or inappropriate"],
	["hate", "Hate or slurs"],
	["self_harm", "Someone might be in danger"],
	["personal_info", "Sharing personal info"],
	["spam", "Spam or scam"],
	["other", "Other"],
];
export const REPORT_NOTE = "Anything else moderators should know? (optional)";
export const REPORT_BLOCK = "Also block this person";
export const REPORT_SEND = "Send report";
export const REPORT_PICK = "Pick a reason first.";

// Rooms, the shared focus timer and presence (docs/chat/BETA-2026-10.md)
export const RETIRED = "This room is closed. Moderators can still read it.";
export const RETIRED_TAG = "Archived";
export const ARCHIVED = (n) => "Archived (" + n + ")";
export const ARCHIVED_HINT = "Old rooms moderators can still read";
export const PHASE = { focus: "Focus", break: "Break" };
export const PLAN = (plan) => { const [f, b] = String(plan).split("/"); return f + " min focus, " + b + " min break"; };
export const FOCUSING = (n) => n + " focusing";
export const TIMER_LABEL = (phase, left) => phase + " " + left + " left";
export const START_FOCUS = (plan) => "Start a focus" + (plan ? " (" + plan.split("/")[0] + " min)" : "");
export const FOCUS_WITH = "Focus with the room";
export const STOP_FOCUSING = "Stop focusing";
export const STAFF_STOP = "Stop timer";
export const STAFF_STOP_Q = "Stop the focus timer for everyone in this room?";
export const NEXT_BREAK = (min) => (min <= 1 ? "Next break in under a minute" : "Next break in " + min + " min");
export const FOCUS_STARTED = "Focus started. Study Hall keeps counting you while you work in another tab.";
export const BREAK_NOW = "Break time. Stretch, then check in.";
export const FOCUS_NOW = "Focus time. Heads down.";
export const TIMER_RESET = "A moderator reset this room's focus timer.";
export const CHECKIN_PLACEHOLDER = "Check in: your goal for this block";
export const CHECKIN_HINT = "During a focus block you can post one short check-in. Chat opens at the break.";
export const RAIL_FOCUS = (n) => (n ? n + " focusing" : "Focus running");
export const PRES = ["Online", "In ", "Focusing"];
export const PRES_TIP = "Study Hall only shows Online, a room, or Focusing. It never shows what site you're on.";
export const PRES_NONE = "No status to show.";
export const SHOW_STATUS = "Show my status";
export const SHOW_STATUS_HINT = "Others can see Online, the room you're in, or Focusing. Never a site or a page. Turn it off to show nothing.";
export const CARD_TITLE = (h) => h;
export const VIEW_PROFILE = "Name card";

// How each room works: a short card at the top of the room, dismissible on this device.
export const HOW_TITLE = "How this room works";
export const HOW_DISMISS = "Got it";
export const HOW = {
	announcements: ["News and updates from the Re:Vault team.", "Only the team posts here. You can react."],
	math: ["Ask about any math, from algebra to calculus.", "Explain how, don't just post answers.", "The room timer runs 25 minutes of focus, then a 5 minute break. Join it any time."],
	science: ["Biology, chemistry, physics, earth science and coding.", "Explain how, don't just post answers.", "The room timer runs 25 minutes of focus, then a 5 minute break. Join it any time."],
	english: ["Reading, essays, grammar, history and languages.", "Help with writing, don't write it for someone.", "The room timer runs 25 minutes of focus, then a 5 minute break. Join it any time."],
	quiet: ["A quiet room for getting work done.", "During a focus block you get one short check-in. Chat opens at the break.", "Blocks are 50 minutes of focus, then a 10 minute break."],
	hangout: ["Off-topic chat, still school-appropriate.", "Be kind. Moderators are here too.", "Read-only late at night (quiet hours)."],
};
// The room's pinned resources and question of the day.
export const RESOURCES = "Resources";
export const OPEN_TOOL = (name) => name + " (opens in a Re:Vault tab)";
export const QOTD = "Question of the day";
export const QOTD_HIDE = "Hide";
export const QOTD_POST = "Post question of the day";
export const QOTD_HINT = "Pinned in this room for 24 hours. Up to 300 characters.";
export const QOTD_ROOMS = "Questions of the day go in Math, Science or English.";
export const ROOM_SOUND = "Room sound";
export const ROOM_SOUND_HINT = "Plays a soft soundscape while you focus with the room. Your own music always wins.";
export const MENU = "Room menu";
export const MENU_ME = "Your name, status and data";

// Reactions: drawn as icons, named in words, so they look the same on every device.
export const REACTION_NAMES = ["Thumbs up", "Heart", "Laugh", "Celebrate", "Thinking", "Done"];
export const REACT_LABEL = (name, n) => name + ", " + n;

// Polls
export const POLL = "Poll";
export const NEW_POLL = "New poll";
export const INVITE = "Invite a friend";
export const POLL_Q = "Question";
export const POLL_OPT = (i) => "Option " + (i + 1);
export const POLL_ADD = "Add option";
export const POLL_LENGTH = "Open for";
export const POLL_DURS = [[5, "5 minutes"], [15, "15 minutes"], [60, "1 hour"], [0, "Until this focus block ends"]];
export const POLL_POST = "Post poll";
export const POLL_NEED = "Write a question and at least 2 options.";
export const POLL_VOTES = (n) => (n === 1 ? "1 vote" : n + " votes");
export const POLL_FEW = "Results show after 3 votes";
export const POLL_CLOSES = (time) => "Closes " + time;
export const POLL_CLOSED = "Closed";
export const POLL_CLEAR = "Clear my vote";
export const POLL_GONE = "Poll removed";
export const POLL_PRIVATE = "Votes are counted, never shown by name.";

// Error fallbacks, by err.code (the server's err.msg is shown instead whenever it has one)
export const ERR = {
	bad_frame: "Something went wrong. Try again.",
	internal: "Something went wrong. Try again.",
	gate: "Finish the Study Hall setup first.",
	closed: READONLY,
	forbidden: "You can't do that.",
	not_found: "That isn't available anymore.",
	rate_limited: "You're sending messages quickly. Wait a moment.",
	slowmode: "Slow mode is on in this room.",
	busy: BUSY,
	banned: BANNED(""),
	locked: LOCKED,
	lockdown: LOCKDOWN_NEW,
	read_only: ANNOUNCE_ONLY,
	empty: "Type a message first.",
	too_long: "That's too long. Keep it under 1000 characters and 20 lines.",
	edit_window: "Messages can only be edited for 15 minutes.",
	frozen: "A moderator is reviewing this message, so it can't be edited.",
	already: "That's already done.",
	limit: "You've reached the limit for that.",
	timeout: "Not sent yet. Check your connection.",
	bad_handle: "That name can't be used. Try another.",
	short_handle: "Pick a name with at least 2 letters.",
	handle_taken: HANDLE_TAKEN,
	handle_reserved: "That name is reserved. Try another.",
	disconnected: "Not sent yet. It will send when Study Hall reconnects.",
	quiet: "You've checked in. Chat opens at the break.",
	idle: "The room timer isn't running. Start a focus first.",
	gone: "That focus block has ended.",
	no_polls: "Polls can't be posted here right now.",
	poll_open: "This room already has an open poll.",
	poll_closed: "That poll has closed.",
	handle_cooldown: "You changed your name recently. You can change it again in a few days.",
	cooldown: "Wait a few seconds, then try again.",
};

// Mod drawer
export const MOD = {
	title: "Moderation", queue: "Queue", flags: "Flags", audit: "Audit", room: "Room", open: "open", urgent: "urgent",
	empty: "Nothing to review.", load: "Loading…", failed: "Couldn't load that. Try again.",
	reason: "Reason", reasonHint: "Pick a reason or type one (3+ characters).", minutes: "Length",
	presets: ["Bullying", "Sexual content", "Hate", "Personal info", "Spam", "Off-topic", "Other"],
	durations: [[10, "10 minutes"], [60, "1 hour"], [1440, "1 day"], [10080, "7 days"]],
	purge: "Remove recent messages", purgeHours: [[1, "Last hour"], [24, "Last 24 hours"]],
	everywhere: "Also ban from the forum", trust: "Trusted", untrust: "Remove trusted", untimeout: "Lift timeout", unban: "Unban",
	dismiss: "Dismiss", freeze: "Freeze", escalate: "Escalate", resolve: "Mark reviewed", context: "Context",
	slow: "Slow mode", off: "Off", lock: "Lock room", unlock: "Unlock room", topic: "Topic", setTopic: "Set topic",
	quiet: "Quiet hours. Setting Off or unlocking Hangout lifts them for tonight.",
	lockdown: "Lockdown", level: ["Off", "1: newer accounts read-only", "2: accounts under 7 days read-only", "3: only moderators post"],
	apply: "Apply", mode: "Mode", modes: ["open", "beta", "readonly", "staff", "off"], mods: "Moderator",
	modeNames: { open: "Open to everyone", beta: "Beta", readonly: "Read only", staff: "Staff only", off: "Off" },
	auditEmpty: "No actions yet.",
	beta: "Beta members", betaCount: (n, cap) => n + " of " + cap, betaName: "Name to add", betaAdd: "Add",
	betaRemove: (name) => "Remove " + name, betaRemoveShort: "Remove", betaEmpty: "No one added yet.",
	betaHint: "Add people by their Study Hall name. Someone you remove leaves within a minute.",
	grant: "Make moderator", revoke: "Remove moderator", ageReset: "Reset age check", done: "Done.",
	support: "Support", blocked: "Blocked attempts", words: (n) => "Word filter, last 24 hours: " + n, reports: (n) => (n === 1 ? "1 report" : n + " reports"),
	// Automatic alerts (the same ones the Discord feed gets), so one moderator can clear them here too.
	alerts: "Alerts",
	alertCats: {
		raid: "Raid lockdown turned on",
		auto_timeout: "Automatic timeout",
		watch: "Watch: keeps asking for personal info or other apps",
		system: "Study Hall system problem",
		safety: "Safety: may be reaching for personal details, secrecy or sexual talk",
		safety_hold: "Held for review: may be reaching for personal details or secrecy",
	},
	heldHint: "Only the sender can see this message until you approve it.",
	attempts: "Sexual words, not posted (kept 30 days)",
	approved: "Approved",

	history: "Name history", sanctions: "Sanctions", strikes: "Strikes", messages: "Recent messages",
	joined: "Joined", probation: "Probation", trusted: "Trusted", age: "Age", none: "None",
	openQ: "Open", recent: "Recent", rev: "Earlier version", yes: "Yes", no: "No", messageCount: "Messages", current: "Now", lifted: "lifted",
	escalation: "Escalation", cybertip: "Report to NCMEC CyberTipline", danger: "For threats of violence or immediate danger, contact local law enforcement.",
};

// The line to show for a failed op: the server's fixed copy when it sent one (`err.msg` is always
// fixed copy from its copy.js, never an exception message), else ours for the code.
export function errText(e) {
	if (e && typeof e.msg === "string" && e.msg) return e.msg;
	const code = e && e.code;
	if (code === "automod" && e.cat && Object.prototype.hasOwnProperty.call(AUTOMOD, e.cat)) return AUTOMOD[e.cat];
	return (typeof code === "string" && Object.prototype.hasOwnProperty.call(ERR, code) && ERR[code]) || ERR.internal;
}
